/* eslint-disable no-use-before-define */
import get from 'lodash/get';
import includes from 'lodash/includes';
import isObject from 'lodash/isObject';
import keys from 'lodash/keys';
import set from 'lodash/set';
import T from 'prop-types';
import React from 'react';
import {
  DynamicFormCustomComponent,
  ErrorCustomComponentPreviouslyRendered,
} from './DynamicFormCustomComponent';
import { DynamicFormCustomPage } from './DynamicFormCustomPage';
import {
  CustomUIComponentRendered,
  DynamicFormCustomUI,
} from './DynamicFormCustomUI';
import { DynamicFormErrorBoundary } from './DynamicFormErrorBoundary';
import { DynamicFormField } from './DynamicFormField';
import { DynamicFormGroup } from './DynamicFormGroup';
import { DynamicFormInput } from './DynamicFormInput';
import { DynamicFormPage } from './DynamicFormPage';
import { DynamicFormSection } from './DynamicFormSection';
import {
  DF_KEYWORD_CUSTOM_FIELD,
  DF_KEYWORD_CUSTOM_INPUT,
  DF_KEYWORD_CUSTOM_PAGE,
  DF_KEYWORD_CUSTOM_UI,
  DF_KEYWORD_SECTIONS,
  DF_KEYWORD_SORT,
} from './constants';
import {
  sortInputs,
  sortInputsIntoSections,
  sortObjectPropertyNames,
} from './operators';
import {
  combineProperties,
  memoizedDependencyMatcher,
  pageLevelDependenciesReducer,
} from './propertyDependencies';
import {
  extendKeyPath,
  formNameFromKeyPath,
  getPageLevelSchemaPath,
  getSchemaAtCursor,
  getSchemaPath,
  isReadOnlyValue,
  matchDependantPropertyName,
  requiredArg,
} from './utils';

const sortContext = {};
const fieldContext = {};
const sectionContext = {};
let formStructure = {};

export const createRenderer = ({
  validate = requiredArg('validate'),
  customComponents = {},
  customPageComponents = {},
  customUIComponents = {},
  excludePages = [],
  bindSubmitForm,
  showNavigation = true,
} = {}) => {
  let pageLevelErrorContext = {};
  let getActivePageLevelDependencies = {};
  validate.fieldNameSchemaPathMap = {};

  // update form structure based on incoming values
  const setFormStructure = (values, path = []) => {
    for (let key in values) {
      if (isObject(values[key])) {
        setFormStructure(values[key], [...path, key]);
      } else if (values.hasOwnProperty(key)) {
        set(formStructure, [...path, key], true);
      }
    }
  };

  const getComponentProps = passthrough => ({
    validate,
    customComponents,
    renderChildren,
    ...passthrough,
  });

  const setSortOrderForFieldNames = (fieldName, sortOrder) =>
    (sortContext[fieldName] = sortOrder);

  const getSortOrderForFieldNames = (fieldName, callback) => {
    if (callback) {
      return callback(sortContext);
    }

    return sortContext[fieldName];
  };

  const setMetadataForFieldName = (fieldName, metadata) =>
    (fieldContext[fieldName] = metadata);

  const getMetadataForFieldName = fieldName => fieldContext[fieldName];
  const setPageLevelErrors = errors => (pageLevelErrorContext = errors);
  const getPageLevelErrors = () => pageLevelErrorContext;

  const setSectionsForPageName = (pageName, sections) =>
    (sectionContext[pageName] = sections);

  const getSectionsForPageName = pageName => sectionContext[pageName];
  const getFormStructure = () => formStructure;

  const updateFormStructureForPath = (keyPath, propertyNames) => {
    const path = formNameFromKeyPath(keyPath);

    set(formStructure, path, {});

    propertyNames.forEach(name => set(formStructure, [...path, name], true));
  };

  const getPageLevelDependencies = memoizedDependencyMatcher(validate.schema);

  const renderChildren = ({
    context = requiredArg('context'),
    formValues = context.parent !== 'DynamicForm'
      ? requiredArg('formValues')
      : null,
    pagesConfig,
  } = {}) => {
    const {
      parent = requiredArg('context.parent'),
      keyPath = requiredArg('context.keyPath'),
      schemaPath = requiredArg('context.schemaPath'),
    } = context;

    const schemaNode =
      schemaPath.length === 0
        ? validate.schema
        : getSchemaAtCursor(schemaPath, validate.schema);

    if (!schemaNode) {
      throw new Error(`No schemaNode at path "${schemaPath.join('.')}"`);
    }

    if (schemaNode[DF_KEYWORD_CUSTOM_UI]) {
      try {
        return DynamicFormCustomUI(
          getComponentProps({
            context: { ...context, keyPath, schemaPath },
            customUIComponents,
            formValues,
            schemaNode,
          })
        );
      } catch (error) {
        if (!(error instanceof CustomUIComponentRendered)) {
          return <DynamicFormErrorBoundary error={error} />;
        }
      }
    }

    if (
      schemaNode[DF_KEYWORD_CUSTOM_FIELD] ||
      schemaNode[DF_KEYWORD_CUSTOM_INPUT]
    ) {
      try {
        return DynamicFormCustomComponent(
          getComponentProps({
            context,
            formValues,
            schemaNode,
            customComponents,
            getComponentProps,
            setMetadataForFieldName,
            getMetadataForFieldName,
            updateFormStructureForPath,
            getPageLevelErrors,
          })
        );
      } catch (error) {
        if (!(error instanceof ErrorCustomComponentPreviouslyRendered)) {
          return <DynamicFormErrorBoundary error={error} />;
        }
      }
    }

    if (schemaNode.type === 'object') {
      if (
        parent === 'DynamicFormPage' &&
        getPageLevelDependencies.matcherLength
      ) {
        const pageLevelDependencies = getPageLevelDependencies(
          formValues,
          true
        );

        getActivePageLevelDependencies = pageLevelDependenciesReducer(
          pageLevelDependencies
        );
      }

      if (parent === 'DynamicForm') {
        return keys(get(schemaNode, 'properties', {}))
          .filter(pageName => !includes(excludePages, pageName))
          .map(pageName => {
            const subSchemaPath = ['properties', pageName];

            const subSchemaNode = getSchemaAtCursor(
              subSchemaPath,
              validate.schema
            );

            const dynamicFormPageProps = {
              context: {
                ...context,
                parent: 'DynamicFormPage',
                keyPath: { pageName },
                schemaPath: subSchemaPath,
              },
              setPageLevelErrors,
              getMetadataForFieldName,
              getFormStructure,
              getSectionsForPageName,
              getSortOrderForFieldNames,
              pageConfig: get(pagesConfig, `${pageName}`, {}),
            };

            if (subSchemaNode[DF_KEYWORD_CUSTOM_PAGE]) {
              return DynamicFormCustomPage(
                getComponentProps({
                  ...dynamicFormPageProps,
                  schemaNode: subSchemaNode,
                  customPageComponents,
                })
              );
            } else {
              return DynamicFormPage(
                getComponentProps({
                  ...dynamicFormPageProps,
                  formValues,
                  bindSubmitForm,
                  showNavigation,
                })
              );
            }
          });
      }

      const getActiveDependencies = memoizedDependencyMatcher(schemaNode);

      const activeDependencies = getActiveDependencies.matcherLength
        ? getActiveDependencies(
            get(formValues, formNameFromKeyPath(keyPath), formValues)
          )
        : [];

      const pageLevelActiveDependencies =
        getActivePageLevelDependencies.dependenciesLength
          ? getActivePageLevelDependencies(keyPath.pageName)
          : [];

      const { properties: combinedProperties, propertyNames } =
        combineProperties(
          schemaNode.properties,
          activeDependencies,
          pageLevelActiveDependencies
        );

      updateFormStructureForPath(keyPath, propertyNames);

      if (schemaNode[DF_KEYWORD_SECTIONS]) {
        const sections = sortInputsIntoSections(
          propertyNames,
          schemaNode[DF_KEYWORD_SECTIONS]
        );

        setSectionsForPageName(keyPath.pageName, sections);

        return sections.map(({ items, sectionName }) => {
          const componentProps = getComponentProps({
            context: {
              ...context,
              parent: 'DynamicFormSection',
              keyPath: extendKeyPath(keyPath, { sectionName }),
            },
            formValues,
          });

          const children = items.reduce((acc, propertyName) => {
            const getDFSectionProps = (
              getSchemaPathForDFSection,
              activeDependencies
            ) => ({
              context: {
                parent: 'DynamicFormSection',
                schemaPath: getSchemaPathForDFSection({
                  schemaPath,
                  propertyName,
                  activeDependencies,
                }),
                keyPath: extendKeyPath(keyPath, {
                  sectionName: sectionName,
                  inputName: propertyName,
                }),
              },
            });

            return [
              ...acc,
              ...(matchDependantPropertyName(
                activeDependencies,
                propertyName
              ) ||
              (schemaNode.properties &&
                typeof schemaNode.properties[propertyName] !== 'undefined') ||
              !schemaNode.properties
                ? [getDFSectionProps(getSchemaPath, activeDependencies)]
                : []),
              ...(matchDependantPropertyName(
                pageLevelActiveDependencies,
                propertyName
              )
                ? [
                    getDFSectionProps(
                      getPageLevelSchemaPath,
                      pageLevelActiveDependencies
                    ),
                  ]
                : []),
            ];
          }, []);

          return (
            <DynamicFormErrorBoundary key={`DFEB-${sectionName}`}>
              <DynamicFormSection
                {...{
                  ...componentProps,
                  children,
                }}
              />
            </DynamicFormErrorBoundary>
          );
        });
      } else {
        let groupPropertyNames = propertyNames;

        if (schemaNode[DF_KEYWORD_SORT]) {
          groupPropertyNames = sortInputs(
            propertyNames,
            schemaNode[DF_KEYWORD_SORT]
          );

          setSortOrderForFieldNames(
            formNameFromKeyPath(keyPath).join('.'),
            groupPropertyNames
          );
        }

        return (
          <DynamicFormErrorBoundary>
            <DynamicFormGroup
              {...getComponentProps({
                context: {
                  ...context,
                  parent: 'DynamicFormGroup',
                  keyPath,
                  schemaPath,
                },
                formValues,
                schemaNode,
                activeDependencies,
                pageLevelActiveDependencies,
                setMetadataForFieldName,
                getPageLevelErrors,
                combinedProperties: sortObjectPropertyNames(
                  combinedProperties,
                  groupPropertyNames
                ),
              })}
            />
          </DynamicFormErrorBoundary>
        );
      }
    }

    if (schemaNode.type === 'array') {
      return (
        <DynamicFormErrorBoundary>
          <DynamicFormGroup
            {...getComponentProps({
              context: {
                ...context,
                parent: 'DynamicFormGroup',
                keyPath,
                schemaPath,
              },
              formValues,
              schemaNode,
              setMetadataForFieldName,
              getPageLevelErrors,
            })}
          />
        </DynamicFormErrorBoundary>
      );
    }

    if (includes(['string', 'boolean', 'number', 'integer'], schemaNode.type)) {
      const readOnly = isReadOnlyValue(
        formValues.readOnly,
        formNameFromKeyPath(keyPath)
      );

      if (parent === 'DynamicFormField') {
        return (
          <DynamicFormErrorBoundary>
            <DynamicFormInput
              {...getComponentProps({
                context: { ...context, parent, keyPath, schemaPath },
                formValues,
                schemaNode,
              })}
              getMetadataForFieldName={getMetadataForFieldName}
              readOnly={readOnly}
              setMetadataForFieldName={setMetadataForFieldName}
            />
          </DynamicFormErrorBoundary>
        );
      } else {
        return (
          <>
            <DynamicFormErrorBoundary>
              <DynamicFormField
                key={[...schemaPath, 'DynamicFormField']}
                {...getComponentProps({
                  context: {
                    ...context,
                    parent: 'DynamicFormField',
                    keyPath,
                    schemaPath,
                  },
                  formValues,
                  schemaNode,
                  getPageLevelErrors,
                })}
                readOnly={readOnly}
              />
            </DynamicFormErrorBoundary>
          </>
        );
      }
    }

    if (schemaNode.type === 'null') {
      return null;
    }

    if (global.__DEV__) {
      return <pre>{JSON.stringify({ context, schemaNode }, null, 2)}</pre>;
    }

    return null;
  };

  renderChildren.propTypes = {
    context: T.object,
    formValues: T.object,
    pagesConfig: T.object,
  };

  return {
    renderChildren,
    getMetadataForFieldName,
    getSectionsForPageName,
    getSortOrderForFieldNames,
    getFormStructure,
    setPageLevelErrors,
    setFormStructure,
  };
};
